/**
 * What the agent changed on an element it updated, field by field.
 *
 * The backend keeps each moved field's old value under the provenance
 * metadata's `previous` (PR 528): a top-level field by its name, and a core
 * field the element holds only inside `core_fields` nested as
 * `core_fields.<key>`. The editor marks each such field by kind and offers the
 * old value back.
 */
import { AGENT_PROVENANCE_SOURCE } from '../pnid/agentReviewItems';
import type { MarkingProvenance } from '../types';

export type FieldChangeKind = 'added' | 'modified' | 'cleared';

export interface FieldChange {
  kind: FieldChangeKind;
  previous: unknown;
}

/** Empty is what the SME reads as "nothing there": null, undefined, '' or []. */
export function isEmptyValue(value: unknown): boolean {
  return value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0);
}

/** Text for an old value in a field's note. */
export function fieldChangeText(value: unknown): string {
  if (isEmptyValue(value)) return 'empty';
  if (typeof value === 'number') return Number.isInteger(value) ? String(value) : value.toFixed(3);
  // A linked P&ID reads as its drawing number and link status, not as JSON.
  if (isRecord(value) && typeof value.pnid_no === 'string') {
    return value.status ? `${value.pnid_no} (${String(value.status)})` : value.pnid_no;
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function sameValue(a: unknown, b: unknown): boolean {
  return a === b || JSON.stringify(a) === JSON.stringify(b);
}

function changeOf(previous: unknown, current: unknown): FieldChange | null {
  const wasEmpty = isEmptyValue(previous);
  const isEmpty = isEmptyValue(current);
  if (wasEmpty && isEmpty) return null;
  if (wasEmpty) return { kind: 'added', previous };
  if (isEmpty) return { kind: 'cleared', previous };
  return sameValue(previous, current) ? null : { kind: 'modified', previous };
}

/** Fields the server sets on every element; never the agent's doing, so never marked. */
const SERVER_SET_FIELDS = new Set([
  'id', 'status', 'provenance', 'reviewed_by', 'reviewed_at', 'created_at', 'updated_at', 'generated_id', 'core_fields',
]);

/**
 * The changes keyed by field path: `description`, `core_fields.notes`, or
 * `pnid_hyperlink.target_node_id`.
 *
 * An object value (`core_fields`, a linked P&ID) is diffed one level down and
 * keyed per inner field, so each inner input carries its own mark; the whole
 * object is keyed by its own name only when it was added or cleared outright.
 * No `previous` at all is an element the agent created: every filled field is
 * added. `previous: {}` is an update that changed nothing: no marks.
 */
export function agentFieldChanges(
  previous: Record<string, unknown> | undefined,
  current: Record<string, unknown>,
): Map<string, FieldChange> {
  const changes = new Map<string, FieldChange>();
  if (!previous) {
    for (const [field, value] of Object.entries(current)) {
      if (SERVER_SET_FIELDS.has(field) && field !== 'core_fields') continue;
      if (isRecord(value)) {
        for (const [key, inner] of Object.entries(value)) {
          if (!isEmptyValue(inner)) changes.set(`${field}.${key}`, { kind: 'added', previous: null });
        }
      } else if (!isEmptyValue(value)) {
        changes.set(field, { kind: 'added', previous: null });
      }
    }
    return changes;
  }
  for (const [field, before] of Object.entries(previous)) {
    const after = current[field];
    if (isRecord(before) && isRecord(after)) {
      for (const key of new Set([...Object.keys(before), ...Object.keys(after)])) {
        const change = changeOf(before[key] ?? null, after[key]);
        if (change) changes.set(`${field}.${key}`, change);
      }
      continue;
    }
    if (isRecord(before) && isEmptyValue(after)) {
      // An inner-only diff cannot say "the whole link is gone"; the object's own name does.
      changes.set(field, { kind: 'cleared', previous: before });
      continue;
    }
    const change = changeOf(before, after);
    if (change) changes.set(field, change);
  }
  return changes;
}

/**
 * The agent's changes on a stored element, for an editor to mark; empty unless
 * the agent wrote the element. The diff reads a missing `previous` as an agent
 * create, and an SME's or the pipeline's element carries no `previous` either.
 */
export function agentChangesOn(element: { provenance?: MarkingProvenance | null }): Map<string, FieldChange> {
  if (element.provenance?.source !== AGENT_PROVENANCE_SOURCE) return new Map();
  return agentFieldChanges(
    element.provenance.metadata?.previous as Record<string, unknown> | undefined,
    element as unknown as Record<string, unknown>,
  );
}
